import React, { useEffect, useMemo, useState } from 'react';
import EventCreateForm from './admin/EventCreateForm';
import EventSelector from './admin/EventSelector';
import RegistrationTable from './admin/RegistrationTable';

function FighterList({ mode = 'manage' }) {
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [registrations, setRegistrations] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingRegistrations, setLoadingRegistrations] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingEvent, setEditingEvent] = useState(null);

    const selectedEvent = useMemo(() => {
        return events.find((e) => String(e._id) === String(selectedEventId)) || null;
    }, [events, selectedEventId]);

    async function apiFetch(url, options = {}) {
        const res = await fetch(url, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers || {})
            },
            ...options
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
            const errorObj = new Error(data?.message || data?.error || 'Request failed');
            errorObj.payload = data;
            throw errorObj;
        }

        return data;
    }

    async function loadEvents() {
        setLoadingEvents(true);
        setError('');

        try {
            const data = await apiFetch('/api/admin/events', { method: 'GET' });
            const nextEvents = Array.isArray(data?.events) ? data.events : [];
            setEvents(nextEvents);

            if (nextEvents.length > 0) {
                const stillExists = nextEvents.some((e) => String(e._id) === String(selectedEventId));
                if (!selectedEventId || !stillExists) {
                    setSelectedEventId(nextEvents[0]._id);
                }
            } else {
                setSelectedEventId('');
            }
        } catch (err) {
            console.error('Failed to load admin events:', err);
            setError('Unable to load admin events.');
        } finally {
            setLoadingEvents(false);
        }
    }

    async function loadRegistrations(eventId) {
        if (!eventId) {
            setRegistrations([]);
            return;
        }

        setLoadingRegistrations(true);
        setError('');

        try {
            const data = await apiFetch(`/api/admin/events/${eventId}/registrations`, {
                method: 'GET'
            });

            setRegistrations(Array.isArray(data?.registrations) ? data.registrations : []);
        } catch (err) {
            console.error('Failed to load registrations:', err);
            setError('Unable to load event registrations.');
            setRegistrations([]);
        } finally {
            setLoadingRegistrations(false);
        }
    }

    useEffect(() => {
        loadEvents();
    }, []);

    useEffect(() => {
        if (mode === 'manage' && selectedEventId) {
            loadRegistrations(selectedEventId);
        } else {
            setRegistrations([]);
        }
    }, [selectedEventId, mode]);

    async function handleCreateEvent(payload) {
        setSaving(true);
        setError('');

        try {
            const data = await apiFetch('/api/admin/events/create', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            const newEvent = data?.event;
            await loadEvents();

            if (newEvent?._id) {
                setSelectedEventId(newEvent._id);
            }
        } catch (err) {
            console.error('Create event failed:', err);
            throw err;
        } finally {
            setSaving(false);
        }
    }

    async function handleUpdateEvent(eventId, payload) {
        setSaving(true);
        setError('');

        try {
            await apiFetch(`/api/admin/events/${eventId}/update`, {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            await loadEvents();

            if (eventId) {
                setSelectedEventId(eventId);
                await loadRegistrations(eventId);
            }

            setEditingEvent(null);
        } catch (err) {
            console.error('Update event failed:', err);
            throw err;
        } finally {
            setSaving(false);
        }
    }

    async function handleRegistrationToggle() {
        if (!selectedEvent) return;

        setSaving(true);
        setError('');

        try {
            const nextStatus = selectedEvent.status === 'published' ? 'closed' : 'published';

            await apiFetch(`/api/admin/events/${selectedEvent._id}/update`, {
                method: 'POST',
                body: JSON.stringify({ status: nextStatus })
            });

            await loadEvents();
            await loadRegistrations(selectedEvent._id);
        } catch (err) {
            console.error('Failed to update event:', err);
            setError(err.message || 'Failed to update event.');
        } finally {
            setSaving(false);
        }
    }

    async function handleRegistrationAction(registrationId, status) {
        setSaving(true);
        setError('');

        try {
            await apiFetch(`/api/admin/registrations/${registrationId}/update`, {
                method: 'POST',
                body: JSON.stringify({ status })
            });

            await loadRegistrations(selectedEventId);
            await loadEvents();
        } catch (err) {
            console.error('Failed to update registration:', err);
            setError(err.message || 'Failed to update registration.');
        } finally {
            setSaving(false);
        }
    }

    function startEditingSelectedEvent() {
        if (!selectedEvent) return;
        setEditingEvent(selectedEvent);
    }

    function cancelEditingEvent() {
        setEditingEvent(null);
    }

    if (loadingEvents) {
        return <div className="loader">LOADING ADMIN PANEL...</div>;
    }

    return (
        <div className="fighter-list-admin">
            {error && (
                <div style={{ color: '#ff6b6b', marginBottom: '1rem', textAlign: 'left' }}>
                    {error}
                </div>
            )}

            <div className="admin-header" style={{ marginBottom: '1rem' }}>
                <h3>EVENTS ({events.length})</h3>
                <button type="button" onClick={loadEvents} className="refresh-btn">↻</button>
            </div>

            {mode === 'create' && (
                <EventCreateForm
                    saving={saving}
                    onSubmitEvent={handleCreateEvent}
                    submitLabel="Create Event"
                    mode="create"
                />
            )}

            {mode === 'manage' && !editingEvent && (
                <>
                    <EventSelector
                        events={events}
                        selectedEventId={selectedEventId}
                        onSelectEvent={setSelectedEventId}
                        selectedEvent={selectedEvent}
                        saving={saving}
                        onRegistrationToggle={handleRegistrationToggle}
                        onEditEvent={startEditingSelectedEvent}
                    />

                    <RegistrationTable
                        registrations={registrations}
                        loading={loadingRegistrations}
                        selectedEventId={selectedEventId}
                        onRefresh={() => loadRegistrations(selectedEventId)}
                        onAction={handleRegistrationAction}
                    />
                </>
            )}

            {mode === 'manage' && editingEvent && (
                <EventCreateForm
                    saving={saving}
                    onSubmitEvent={(payload) => handleUpdateEvent(editingEvent._id, payload)}
                    submitLabel="Save Changes"
                    mode="edit"
                    initialEvent={editingEvent}
                    onCancel={cancelEditingEvent}
                />
            )}

            {mode !== 'create' && mode !== 'manage' && (
                <div className="action-row">
                    <div className="cta-text" style={{ width: '100%' }}>
                        <p>Admin Mode</p>
                        <div style={{ fontSize: '1rem', marginTop: '0.6rem' }}>
                            Select an admin mode from the menu.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FighterList;